import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { Ref } from 'vue';
import {
  STOCK_CHANGED_EVENT,
  stockChangeConcernsProduct,
} from '@/domain/catalog/catalog-events';
import type { IStockChangedEvent } from '@/domain/catalog/catalog-events';
import {
  IStockRow,
  sameStockLines,
  stockRowsChanged,
  stockRowsFromRecipes,
} from '@/domain/catalog/stock-form';
import type { IStockRecipe } from '@/interfaces/IStockRecipe';
import { EventBus } from '@/modules/EventBus';

/** The product, store and branch whose stock lines are read. */
export interface IStockLinesTarget {
  restaurantId: number;
  branchId: number;
  itemId: number;
}

export interface IStockLinesOptions {
  /** The editor's product, store and current branch; null while not known. */
  context: () => {
    restaurantId: number | null;
    branchId: number | null;
    itemId: number | null;
  };
  /** The editor is saving: a change is followed after, or before the stock save. */
  saving: Readonly<Ref<boolean>>;
  /** Reads the saved lines of a product in a branch. */
  read: (target: IStockLinesTarget) => Promise<IStockRecipe[]>;
}

/**
 * The lines of the product editor's «Stock» (plan §5.12) and how they follow
 * an inventory change of the assistant panel (plan P5-3,
 * `STOCK_CHANGED_EVENT`): clean lines are read again; edited ones that no
 * longer match what is saved are `stale` until they are read again, and
 * `beforeSave` refuses them, so a save never undoes the change nor repeats
 * a supply. A change that comes while the editor saves or the lines are
 * read again is followed after it, or before the stock part of that save.
 */
export function useStockLines(options: IStockLinesOptions) {
  let disposed = false;
  let sequence = 0;
  /** A change came while busy: followed after, or before the stock save. */
  let followPending = false;
  /** The read of a followed change while it runs (a save waits for it). */
  let following: Promise<void> | null = null;

  /** The branch the lines were read for: they are saved there. */
  const loadedBranchId = ref<number | null>(null);
  /** The lines as read: what the edits are made on. */
  const base = shallowRef<IStockRow[] | null>(null);
  const rows = ref<IStockRow[]>([]);
  /** The first read failed: nothing to show. */
  const loadFailed = ref(false);
  /** What is saved changed after the edited lines were read. */
  const stale = ref(false);
  /** «Actualizar» is reading the lines again. */
  const reloading = ref(false);

  const dirty = computed(
    () => !!base.value && stockRowsChanged(base.value, rows.value)
  );
  /** Inputs are disabled while the editor saves or the lines are read again. */
  const busy = computed(() => options.saving.value || reloading.value);

  /** The lines as read, for `branchId`: nothing is edited, nothing is stale. */
  function show(recipes: IStockRecipe[], branchId: number) {
    const loaded = stockRowsFromRecipes(recipes);
    base.value = loaded;
    rows.value = [...loaded];
    loadedBranchId.value = branchId;
    loadFailed.value = false;
    stale.value = false;
  }

  /** In `branchId` (else the page's branch), when everything is known. */
  function targetIn(branchId: number | null): IStockLinesTarget | null {
    const context = options.context();
    const branch = branchId ?? context.branchId;
    if (
      context.itemId === null ||
      context.restaurantId === null ||
      branch === null
    )
      return null;
    return {
      restaurantId: context.restaurantId,
      branchId: branch,
      itemId: context.itemId,
    };
  }

  /** Reads the lines of the page's branch; resolves false when they could not be read. */
  async function load(): Promise<boolean> {
    const target = targetIn(null);
    if (!target) return false;
    sequence += 1;
    const current = sequence;
    try {
      const recipes = await options.read(target);
      if (disposed || current !== sequence) return true;
      show(recipes, target.branchId);
      return true;
    } catch (error) {
      if (disposed || current !== sequence) return true;
      console.error('Loading stock failed', error);
      if (!base.value) loadFailed.value = true;
      return false;
    }
  }

  /**
   * After an assistant change of this stock, in the branch the lines were
   * read for: clean lines show what is saved now; edited ones are stale
   * while what is saved differs from what they were read from (a read that
   * fails counts as different).
   */
  async function followServer() {
    const target = targetIn(loadedBranchId.value);
    if (!target) return;
    sequence += 1;
    const current = sequence;
    let recipes: IStockRecipe[];
    try {
      recipes = await options.read(target);
    } catch (error) {
      if (disposed || current !== sequence) return;
      console.error('Loading stock failed', error);
      if (base.value) stale.value = true;
      else loadFailed.value = true;
      return;
    }
    if (disposed || current !== sequence) return;
    const loaded = base.value;
    if (!dirty.value || !loaded) {
      show(recipes, target.branchId);
      return;
    }
    stale.value = !sameStockLines(loaded, stockRowsFromRecipes(recipes));
  }

  function follow() {
    followPending = false;
    const run = followServer();
    following = run;
    void run.finally(() => {
      if (following === run) following = null;
    });
  }

  /** The assistant panel changed a store's inventory. */
  function onStockChanged(change: IStockChangedEvent) {
    const target = targetIn(loadedBranchId.value);
    if (!target) return;
    const stockItemIds = [...(base.value ?? []), ...rows.value].map(
      (row) => row.stockItemId
    );
    if (!stockChangeConcernsProduct(change, { ...target, stockItemIds }))
      return;
    // A running save or reload reads the lines last: follow the change
    // after it (or before the save's stock part, `beforeSave`).
    if (busy.value) {
      followPending = true;
      return;
    }
    follow();
  }

  watch(busy, (isBusy) => {
    if (!isBusy && followPending) follow();
  });

  /** «Actualizar»: the lines as saved now, in place of the edited ones. */
  async function reload(): Promise<boolean> {
    if (reloading.value) return true;
    reloading.value = true;
    const loaded = await load();
    if (disposed) return true;
    reloading.value = false;
    return loaded;
  }

  /**
   * Before the stock part of a save writes: a change that came during the
   * save (or one still being read) decides first, and stale lines are
   * refused («Guardar» is blocked while stale; «Guardar lo que falta» is not).
   */
  async function beforeSave(): Promise<void> {
    if (followPending) follow();
    if (following) await following;
    if (stale.value)
      throw new Error('The stock changed elsewhere: read it again first.');
  }

  /** «Descartar»: what was read; after an assistant change, read again. */
  function reset() {
    if (base.value) rows.value = [...base.value];
    if (stale.value) void load();
  }

  EventBus.$on(STOCK_CHANGED_EVENT, onStockChanged);
  onScopeDispose(() => {
    disposed = true;
    EventBus.$off(STOCK_CHANGED_EVENT, onStockChanged);
  });

  return {
    loadedBranchId,
    base,
    rows,
    loadFailed,
    stale,
    reloading,
    dirty,
    busy,
    load,
    reload,
    beforeSave,
    reset,
  };
}
